<template>
  <div class="summary-card mt-2">
    <div class="summary-top">
      <div class="summary-avatar">
        <span class="summary-avatar-text">{{ vendorInitials }}</span>
      </div>

      <div class="summary-copy">
        <div class="summary-name-row">
          <p class="summary-name">
            {{ vendor?.name || "Vendor" }}
          </p>
          <v-icon size="16" color="success">mdi-check-circle-outline</v-icon>
        </div>
        <p class="summary-company" v-if="vendor?.company_name">
          {{ vendor?.company_name || "Unknown company" }}
        </p>
        <div v-if="vendor?.address" class="summary-meta">
          <span class="summary-meta-icon">
            <v-icon size="14" color="grey-darken-1">mdi-map-marker-outline</v-icon>
          </span>
          <span>{{ vendor.address }}</span>
        </div>
      </div>
    </div>

    <div class="summary-actions">
      <button type="button" class="summary-action">
        <v-icon size="18" color="deep-orange-darken-1">mdi-message-outline</v-icon>
        <span>Message</span>
      </button>
      <button type="button" class="summary-action summary-action--split">
        <v-icon size="18" color="deep-orange-darken-1">mdi-phone-outline</v-icon>
        <span>Call</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

type Vendor = {
  id: string | number | null;
  name?: string | null;
  vendor_code?: string | null;
  company_name?: string | null;
  phone?: string | null;
  address?: string | null;
};

const props = defineProps<{
  vendor: Vendor | null;
}>();

const vendorInitials = computed(() => {
  const value = props.vendor?.name || "SU";
  return (
    value
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "SU"
  );
});
</script>

<style scoped>
.summary-card {
  padding: 0;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
}

.summary-top {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
}

.summary-avatar {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
  flex: 0 0 auto;
}

.summary-avatar-text {
  font-size: 18px;
  font-weight: 800;
}

.summary-copy {
  min-width: 0;
  flex: 1;
}

.summary-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.summary-name {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-company {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 700;
  color: #16a34a;
  text-transform: capitalize;
}

.summary-meta {
  margin: 4px 0 0;
  display: flex;
  gap: 4px;
  font-size: 14px;
  color: #4b5563;
}

.summary-meta-icon {
  display: inline-flex;
  align-items: center;
}

.summary-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid #e5e7eb;
}

.summary-action {
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.summary-action--split {
  border-left: 1px solid #e5e7eb;
}
</style>
